import {ListQuestion} from '../../shared/models/list-question';
import {AlertService} from '../../shared/services/alert.service';
import { Component, OnInit } from '@angular/core';
import { Question } from '../../shared/models/question';
import { QuestionSet } from '../../shared/models/question-set';
import { AssessmentService } from '../../shared/services/assessment.service';
import { QuestionFactoryService } from '../../shared/services/question-factory.service';
import { Assessment } from '../../shared/models/assessment';
import { Scale } from '../../shared/models/scale';
import { Router, ActivatedRoute } from '@angular/router';
import { QuestionSetLabel } from 'app/shared/models/question-set-label';
import { QuestionType } from 'app/shared/models/question-type';
import { FlaggedCondition } from 'app/shared/models/flagged-condition';
import { QuestionSetType } from "app/shared/models/question-set-type";

@Component({
  selector: 'app-question-edit',
  templateUrl: './question-edit.component.html',
  styleUrls: ['./question-edit.component.css']
})
export class QuestionEditComponent implements OnInit {

    private _question:Question;
  private questionSet:QuestionSet;
  private assessment:Assessment;
  private readonly SAVE_TIME = 1500;
  private readonly ERROR_MESSAGE_TIME = 3000;
  public isSaving:boolean;
  public flagValue:any;
  public flagDirection:any;
  private _flagDirectionList:{id:string, name:string}[];

  constructor(
    private assessmentService:AssessmentService
    ,private questionFactory: QuestionFactoryService
    ,private alertService: AlertService
    ,private router: Router
    ,private route: ActivatedRoute
  ) { 
    this._question = this.questionFactory.createDefaultEmptyQuestion();
    this._flagDirectionList = [
      {id: 'greaterOrEqual', 'name':"Greater than or equal to"}
      ,{id: 'lessOrEqual', 'name':"Less than or equal to"}
    ];

  }

  ngOnInit() {
    this.route.params.subscribe((params) => {
      let assessmentUid = params['uid'];
      let setId = params['setId'];
      let questionId = params['questionId'];

      this.assessmentService.getAssessment(assessmentUid)
        .then((assessment:Assessment) => {
          this.assessment = assessment;
          this.questionSet = assessment.question_sets.find((s) => s.id == setId);
          this._question = (this.questionSet) ? this.questionSet.questions.find((q) => q.id == questionId) : null;
          if (!(this.questionSet && this._question)) {
            // TODO: stephen should we navigate back?? or do some kind of 404?
            this.router.navigate(["not-found"]);
          }
          
          // if only one scale remains on the edit, make sure it's always the most current.
          this.setScaleIfSingle(this.question, this.assessment);

          if (this._question.hasFlagCondition) {
            this.flagValue = this._question.flaggedCondition.flagValues;

            // multiple choice only has a single flag so we go off that.
            if (this.question.type == QuestionType.MultipleChoice) {
              this.flagDirection = this._flagDirectionList.find(f => f.id == this._question.flaggedCondition.flagDirection);
              this.flagValue = this.flagValue[0];
            }
          }
        })
        .catch((error) => {
          console.error(error);
          this.router.navigate(["not-found"]);
        })
    });
  }

  public getQuestionType(question:Question) {
    let type = "UNKNOWN";
    switch (question.type) {
      case QuestionType.Checklist: {
        type = "Checklist";
      }
      break;
      case QuestionType.MultipleChoice: {
        type = "Multiple Choice";
      }
      break;
      case QuestionType.SingleChoice: {
        type = "Single Choice";
      }
      break;
      case QuestionType.Textbox: {
        type = "Textbox";
      }
      break;
    }
    return type;
  }

  private setScaleIfSingle(question:Question, assessment:Assessment) {
    if (assessment && assessment.scales && assessment.scales.length == 1) {
      question.assessment_scale = this.assessment.scales[0];
    }
  }

  get scales() : Scale[] {
    if (this.assessment) {
      return this.assessment.scales;
    }
    return null;
  }

  get question() : Question {
    return this._question;
  }

  get labels() : QuestionSetLabel[] {
    return (this.questionSet ? this.questionSet.labels : []);
  }

  get FlagDirections() : {id: string, name:string}[] {
    return this._flagDirectionList;
  }

  public isChecklistQuestion() : boolean {
    return this.question.type == QuestionType.Checklist;
  }

  public isMultipleChoiceQuestion() : boolean {
    return this.question.type == QuestionType.MultipleChoice;
  }

  public compareIds(a:any, b:any) {
    if (!a || !b) {
      return false;
    }
    return a.id === b.id;
  }

  public toggleFlagQuestion(question:Question) {
    let condition = null;
    if (!question.hasFlagCondition) {
      condition = new FlaggedCondition();
    }
    question.flaggedCondition = condition;
  }

  public hasFlagValue(flagValue:QuestionSetLabel) {
    if (!this.flagValue) {
      return false;
    }

    return this.flagValue.find(fv => fv.id == flagValue.id) != null;
  }

  public toggleChecklistFlagValue(flagValue:QuestionSetLabel) {
    if (this.hasFlagValue(flagValue)) {
      this.flagValue = this.flagValue.filter(fv => fv.id != flagValue.id);
    }
    else {
      if (!this.flagValue) {
        this.flagValue = [];
      }
      this.flagValue.push(flagValue);
    }
  }

  private validateQuestion(question:Question) {
    let errors = [];
    if (!question.prompt || question.prompt.trim() == "") {
      errors.push("question prompt cannot be empty");
    }
    if (question.type != QuestionType.Textbox
        && question.type != QuestionType.SingleChoice) {
      if (question.hasFlagCondition) {
        if (!question.flaggedCondition.flagValues.length) {
          errors.push("Flag criteria must be set, or turn off the flag checkbox");
        }
        if (question.type == QuestionType.MultipleChoice
          && question.flaggedCondition.flagDirection == "") {
            errors.push("Flag direction must be set");
          }
      }
    }
    if (!question.assessment_scale) {
      errors.push("This question must have a scale assigned to it before saving");
    }
    return errors;
    
  }

  private updateFlagValues(question:Question) {
    if (!question.hasFlagCondition) {
      return;
    }
    if (question.type == QuestionType.MultipleChoice) {
      question.flaggedCondition.addFlagValue(this.flagValue);
      question.flaggedCondition.flagDirection = this.flagDirection.id;
    }
    else if (question.type == QuestionType.Checklist) {
      this.flagValue.foreach(i => question.flaggedCondition.addFlagValue(i));
    }
  }

  getAnswerLabels() : QuestionSetLabel[] {
    let labels = this.questionSet.labels;
    if (this.question.type == QuestionType.MultipleChoice
    || this.question.type == QuestionType.Checklist) {
      let listQuestion = this.question as ListQuestion;
      labels = listQuestion.CustomLabels;
    }
    return labels;
  }

  allowCustomLabels() {
    if (this.questionSet 
      && this.questionSet.display_type == QuestionSetType.QUESTION_LIST
      && this.question.type != QuestionType.SingleChoice
      && this.question.type != QuestionType.Textbox) {
        return true;
    }
    return false;
  }

  private saveQuestion(question) {
    this.isSaving = true;
    this.updateFlagValues(question);
    let errors = this.validateQuestion(question);
    if (errors.length) {
      this.alertService.error(errors.join(".\n"), 3000);
      this.isSaving = false;
      return;
    }

		return this.assessmentService.saveAssessment(this.assessment).then((assessment) => {
      this.alertService.success("Question saved", this.SAVE_TIME);
      this.isSaving = false;
      return question;
    }).catch((error) => {
      this.isSaving = false;
      this.alertService.error("An error occurred in saving. Please try again or contact support.", this.ERROR_MESSAGE_TIME);
			console.error(error);
		});
  }

  save() {
    this.saveQuestion(this.question);
	}
	
	saveAndCreateNew() {
    this.saveQuestion(this.question).then((question) => {
      this.router.navigate(["../../", "new"], {relativeTo: this.route, replaceUrl: true});
    });
	}

}



// WEBPACK FOOTER //
// ./src/app/admin/question-edit/question-edit.component.ts